/**
 * NOTE ON convertLegacyRouteRef USAGE.
 * Status: PARTIALLY BLOCKED (upstream exports/typing).
 * Last reviewed: 2026-07-02.
 *
 * This module now has a single remaining `convertLegacyRouteRef(...)` call:
 *   - `@aws/amazon-ecs-plugin-for-backstage` → `routes.root`
 *
 * Kubernetes no longer needs a bridge here because `@backstage/plugin-kubernetes`
 * exposes a native routeRef via `/alpha` (`routes.kubernetes`).
 *
 * Internal plugins (yaml-editor, score-card, adr) and community plugins
 * github-actions/github-issues/datadog already use native routeRefs here.
 */
import { createFrontendModule } from '@backstage/frontend-plugin-api';
import { EntityContentBlueprint } from '@backstage/plugin-catalog-react/alpha';
import { convertLegacyRouteRef } from '@backstage/core-compat-api';
import { Entity } from '@backstage/catalog-model';
import githubActionsPlugin from '@backstage-community/plugin-github-actions/alpha';
import githubIssuesPlugin from '@backstage-community/plugin-github-issues/alpha';
import kubernetesPlugin from '@backstage/plugin-kubernetes/alpha';
import { amazonEcsPlugin } from '@aws/amazon-ecs-plugin-for-backstage';
import { yamlEditorRouteRef } from '@internal/plugin-yaml-editor/alpha';
import { scoreCardRouteRef } from '@internal/plugin-score-card/alpha';
import { adrRouteRef } from '@internal/plugin-adr/alpha';
import datadogPlugin from '@roadiehq/backstage-plugin-datadog/alpha';
import {
  ExpandableNavigation,
  LightBox,
  ReportIssue,
  TextSize,
} from '@backstage/plugin-techdocs-module-addons-contrib';
import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import { Mermaid } from 'backstage-plugin-techdocs-addon-mermaid';
import { techdocsMermaidProps } from '../components/techdocs/mermaidConfig';
import { TechDocsNavScrollFix } from '../components/techdocs/TechDocsNavScrollFix';
import {
  isCicdAvailable,
  isDatadogAvailable,
  isDevOpsMetricsAvailable,
  isEcsAvailable,
  isGraphqlTabAvailable,
  isGithubContentAvailable,
  isKubernetesAvailable,
  isQetaAvailable,
  isTechRadarAvailable,
  isYamlEditorAvailable,
  hasEntityDocsTab,
} from '../utils/featureDetection';
import { isAdrAvailable } from '@internal/plugin-adr';

const isKindValue = (entity: Entity, kind: string) =>
  entity.kind?.toLowerCase() === kind;

const isComponentTypeValue = (entity: Entity, type: string) =>
  isKindValue(entity, 'component') &&
  (entity.spec as { type?: string } | undefined)?.type === type;

const isAnyComponentEntity = (entity: Entity) =>
  isKindValue(entity, 'component');

const isServiceOrWebsiteComponentEntity = (entity: Entity) =>
  isComponentTypeValue(entity, 'service') ||
  isComponentTypeValue(entity, 'website');

const isServiceComponentEntity = (entity: Entity) =>
  isComponentTypeValue(entity, 'service');

const isApiEntity = (entity: Entity) => isKindValue(entity, 'api');

const isSystemEntity = (entity: Entity) => isKindValue(entity, 'system');

const isDomainEntity = (entity: Entity) => isKindValue(entity, 'domain');

const isAgentEntity = (entity: Entity) => isKindValue(entity, 'agent');

// Catch-all for entity kinds without a dedicated overview page (template, location,
// resource, and any custom kind). Mirrors the legacy `defaultEntityPage` catch-all
// in EntityPage.tsx — anything not explicitly handled above gets this tab set.
const isDefaultPageEntity = (entity: Entity) =>
  !isAnyComponentEntity(entity) &&
  !isApiEntity(entity) &&
  !isKindValue(entity, 'user') &&
  !isKindValue(entity, 'group') &&
  !isSystemEntity(entity) &&
  !isDomainEntity(entity) &&
  !isAgentEntity(entity);

const isQetaEntity = (entity: Entity) =>
  isQetaAvailable(entity) &&
  (isAnyComponentEntity(entity) ||
    isApiEntity(entity) ||
    isSystemEntity(entity) ||
    isDomainEntity(entity) ||
    isDefaultPageEntity(entity));

// Equivalent to `isTechDocsAvailable && (component | agent | defaultPage)`, but
// kept in featureDetection so the catalog Document view can deep-link into this
// tab without re-deriving when it exists.
const isTechDocsEntity = hasEntityDocsTab;

const isGithubContentEntity = (entity: Entity) =>
  isGithubContentAvailable(entity) &&
  (isAnyComponentEntity(entity) ||
    isApiEntity(entity) ||
    isDefaultPageEntity(entity));

const isDatadogEntity = (entity: Entity) =>
  (isAnyComponentEntity(entity) || isDefaultPageEntity(entity)) &&
  isDatadogAvailable(entity);

const isDevOpsMetricsEntity = (entity: Entity) =>
  (isAnyComponentEntity(entity) || isDefaultPageEntity(entity)) &&
  isDevOpsMetricsAvailable(entity);

const isYamlEditorEntity = (entity: Entity) =>
  (isAnyComponentEntity(entity) || isDefaultPageEntity(entity)) &&
  isYamlEditorAvailable(entity);

const devopsMetricsEntityContent = EntityContentBlueprint.make({
  name: 'devops-metrics',
  params: {
    path: '/dora-metrics',
    title: 'DORA Metrics',
    filter: isDevOpsMetricsEntity,
    loader: async () => {
      const { DevOpsMetricsComponent } = await import(
        '@internal/plugin-devops-metrics'
      );
      return <DevOpsMetricsComponent />;
    },
  },
});

const yamlEditorEntityContent = EntityContentBlueprint.make({
  name: 'yaml-editor',
  params: {
    path: '/yaml-editor',
    title: 'Config',
    filter: isYamlEditorEntity,
    routeRef: yamlEditorRouteRef,
    loader: async () => {
      const { EntityYamlEditorPageContent } = await import(
        '@internal/plugin-yaml-editor/alpha'
      );
      return <EntityYamlEditorPageContent />;
    },
  },
});

const scoreCardEntityContent = EntityContentBlueprint.make({
  name: 'score-card',
  params: {
    path: '/score-card',
    title: 'Score card',
    filter: entity =>
      isAnyComponentEntity(entity) || isDefaultPageEntity(entity),
    routeRef: scoreCardRouteRef,
    loader: async () => {
      const { ScorecardComponent } = await import(
        '@internal/plugin-score-card/alpha'
      );
      return <ScorecardComponent />;
    },
  },
});

const docsEntityContent = EntityContentBlueprint.make({
  name: 'docs',
  params: {
    path: '/docs',
    title: 'Docs',
    filter: isTechDocsEntity,
    loader: async () => {
      const { CustomEntityTechdocsContent } = await import(
        '../components/techdocs/CustomEntityTechdocsContent'
      );

      return (
        <CustomEntityTechdocsContent>
          <TechDocsAddons>
            <ReportIssue />
            <ExpandableNavigation />
            <TextSize />
            <LightBox />
            <TechDocsNavScrollFix />
            <Mermaid {...techdocsMermaidProps} />
          </TechDocsAddons>
        </CustomEntityTechdocsContent>
      );
    },
  },
});

const feedbackEntityContent = EntityContentBlueprint.make({
  name: 'feedback',
  params: {
    path: '/feedback',
    title: 'Feedback',
    filter: entity =>
      isAnyComponentEntity(entity) ||
      isApiEntity(entity) ||
      isAgentEntity(entity) ||
      isDefaultPageEntity(entity),
    loader: () =>
      import('../components/catalog/EntityFeedbackContent').then(
        ({ EntityFeedbackContent }) => <EntityFeedbackContent />,
      ),
  },
});

const qetaEntityContent = EntityContentBlueprint.make({
  name: 'qeta',
  params: {
    path: '/qeta',
    title: 'Q&A',
    filter: isQetaEntity,
    loader: () =>
      import('../components/catalog/QetaContent').then(({ QetaContent }) => (
        <QetaContent />
      )),
  },
});

const componentOverviewEntityContent = EntityContentBlueprint.make({
  name: 'component-overview',
  params: {
    path: '/',
    title: 'Overview',
    filter: isAnyComponentEntity,
    loader: () =>
      import('../components/catalog/EntityNativeContent').then(
        ({ ComponentEntityOverviewContent }) => (
          <ComponentEntityOverviewContent />
        ),
      ),
  },
});

const apiDefinitionEntityContent = EntityContentBlueprint.make({
  name: 'api-definition',
  params: {
    path: '/',
    title: 'Definition',
    filter: isApiEntity,
    loader: () =>
      import('../components/catalog/EntityNativeContent').then(
        ({ ApiEntityDefinitionContent }) => <ApiEntityDefinitionContent />,
      ),
  },
});

const apiOverviewEntityContent = EntityContentBlueprint.make({
  name: 'api-overview',
  params: {
    path: '/overview',
    title: 'Overview',
    filter: isApiEntity,
    loader: () =>
      import('../components/catalog/EntityNativeContent').then(
        ({ ApiEntityOverviewContent }) => <ApiEntityOverviewContent />,
      ),
  },
});

const userOverviewEntityContent = EntityContentBlueprint.make({
  name: 'user-overview',
  params: {
    path: '/',
    title: 'Overview',
    filter: entity => isKindValue(entity, 'user'),
    loader: () =>
      import('../components/catalog/EntityNativeContent').then(
        ({ UserEntityOverviewContent }) => <UserEntityOverviewContent />,
      ),
  },
});

const groupOverviewEntityContent = EntityContentBlueprint.make({
  name: 'group-overview',
  params: {
    path: '/',
    title: 'Overview',
    filter: entity => isKindValue(entity, 'group'),
    loader: () =>
      import('../components/catalog/EntityNativeContent').then(
        ({ GroupEntityOverviewContent }) => <GroupEntityOverviewContent />,
      ),
  },
});

const systemOverviewEntityContent = EntityContentBlueprint.make({
  name: 'system-overview',
  params: {
    path: '/',
    title: 'Overview',
    filter: isSystemEntity,
    loader: () =>
      import('../components/catalog/EntityNativeContent').then(
        ({ SystemEntityOverviewContent }) => <SystemEntityOverviewContent />,
      ),
  },
});

const domainOverviewEntityContent = EntityContentBlueprint.make({
  name: 'domain-overview',
  params: {
    path: '/',
    title: 'Overview',
    filter: isDomainEntity,
    loader: () =>
      import('../components/catalog/EntityNativeContent').then(
        ({ DomainEntityOverviewContent }) => <DomainEntityOverviewContent />,
      ),
  },
});

const agentOverviewEntityContent = EntityContentBlueprint.make({
  name: 'agent-overview',
  params: {
    path: '/',
    title: 'Overview',
    filter: isAgentEntity,
    loader: () =>
      import('../components/catalog/EntityNativeContent').then(
        ({ AgentEntityOverviewContent }) => <AgentEntityOverviewContent />,
      ),
  },
});

// Default-page overview for entity kinds without a dedicated page (template, location, etc.).
const defaultPageOverviewEntityContent = EntityContentBlueprint.make({
  name: 'default-page-overview',
  params: {
    path: '/',
    title: 'Overview',
    filter: isDefaultPageEntity,
    loader: () =>
      import('../components/catalog/EntityNativeContent').then(
        ({ ComponentEntityOverviewContent }) => (
          <ComponentEntityOverviewContent />
        ),
      ),
  },
});

const techRadarEntityContent = EntityContentBlueprint.make({
  name: 'tech-radar',
  params: {
    path: '/tech-radar',
    title: 'Tech Radar',
    filter: entity =>
      (isAnyComponentEntity(entity) || isDefaultPageEntity(entity)) &&
      isTechRadarAvailable(entity),
    loader: () =>
      import('@internal/plugin-tech-radar-aoe').then(
        ({ EntityTechRadarContent }) => <EntityTechRadarContent />,
      ),
  },
});

const kubernetesEntityContent = EntityContentBlueprint.make({
  name: 'kubernetes',
  params: {
    path: '/kubernetes',
    title: 'Kubernetes',
    filter: entity =>
      isServiceOrWebsiteComponentEntity(entity) &&
      isKubernetesAvailable(entity),
    routeRef: kubernetesPlugin.routes.kubernetes,
    loader: () =>
      import('@backstage/plugin-kubernetes').then(
        ({ EntityKubernetesContent }) => <EntityKubernetesContent />,
      ),
  },
});

const ecsEntityContent = EntityContentBlueprint.make({
  name: 'aws-ecs',
  params: {
    path: '/aws/ecs',
    title: 'ECS',
    filter: isEcsAvailable,
    routeRef: convertLegacyRouteRef(amazonEcsPlugin.routes.root),
    loader: () =>
      import('@aws/amazon-ecs-plugin-for-backstage').then(
        ({ EntityAmazonEcsServicesContent }) => (
          <EntityAmazonEcsServicesContent />
        ),
      ),
  },
});

const componentApiEntityContent = EntityContentBlueprint.make({
  name: 'component-api',
  params: {
    path: '/api',
    title: 'API',
    filter: isServiceComponentEntity,
    loader: async () => {
      const [{ Grid }, apiDocsModule, githubActionsModule] = await Promise.all([
        import('@material-ui/core'),
        import('@backstage/plugin-api-docs'),
        import('@backstage-community/plugin-github-actions'),
      ]);

      return (
        <Grid container spacing={3} alignItems="stretch">
          <Grid item md={6}>
            <apiDocsModule.EntityProvidedApisCard />
          </Grid>
          <Grid item md={6}>
            <apiDocsModule.EntityConsumedApisCard />
          </Grid>
          <Grid item md={6}>
            <githubActionsModule.EntityRecentGithubActionsRunsCard limit={4} />
          </Grid>
        </Grid>
      );
    },
  },
});

const dependenciesEntityContent = EntityContentBlueprint.make({
  name: 'dependencies',
  params: {
    path: '/dependencies',
    title: 'Dependencies',
    filter: isServiceOrWebsiteComponentEntity,
    loader: async () => {
      const [{ Grid }, catalogModule] = await Promise.all([
        import('@material-ui/core'),
        import('@backstage/plugin-catalog'),
      ]);

      return (
        <Grid container spacing={3} alignItems="stretch">
          <Grid item md={6}>
            <catalogModule.EntityDependsOnComponentsCard variant="gridItem" />
          </Grid>
          <Grid item md={6}>
            <catalogModule.EntityDependsOnResourcesCard variant="gridItem" />
          </Grid>
        </Grid>
      );
    },
  },
});

const dependabotEntityContent = EntityContentBlueprint.make({
  name: 'dependabot',
  params: {
    path: '/dependabot',
    title: 'Dependabot',
    filter: entity =>
      isGithubContentAvailable(entity) &&
      (isComponentTypeValue(entity, 'website') || isApiEntity(entity)),
    loader: () =>
      import('@roadiehq/backstage-plugin-security-insights').then(
        ({ EntityGithubDependabotContent }) => (
          <EntityGithubDependabotContent />
        ),
      ),
  },
});

const codeInsightsEntityContent = EntityContentBlueprint.make({
  name: 'code-insights',
  params: {
    path: '/code-insights',
    title: 'Code Insights',
    filter: entity =>
      isGithubContentAvailable(entity) &&
      (isAnyComponentEntity(entity) ||
        isApiEntity(entity) ||
        isDefaultPageEntity(entity)),
    loader: async () => {
      const [{ Grid }, githubExplorerModule, githubInsightsModule] =
        await Promise.all([
          import('@material-ui/core'),
          import('@internal/plugin-github-explorer'),
          import('@roadiehq/backstage-plugin-github-insights'),
        ]);

      return (
        <Grid container spacing={3} direction="column" alignItems="stretch">
          <Grid item xs={12}>
            <githubExplorerModule.GitHubStyleContributorsChart />
          </Grid>
          <Grid item xs={12}>
            <githubInsightsModule.EntityGithubInsightsReadmeCard />
          </Grid>
        </Grid>
      );
    },
  },
});

const graphiqlEntityContent = EntityContentBlueprint.make({
  name: 'graphiql',
  params: {
    path: '/graphiql',
    title: 'GraphiQL',
    filter: isGraphqlTabAvailable,
    loader: () =>
      import('../components/catalog/EntityGraphiQLContent').then(
        ({ EntityGraphiQLContent }) => <EntityGraphiQLContent />,
      ),
  },
});

const adrEntityContent = EntityContentBlueprint.make({
  name: 'adr',
  params: {
    path: '/adrs',
    title: 'ADRs',
    filter: entity =>
      isAdrAvailable(entity) &&
      (isAnyComponentEntity(entity) || isDefaultPageEntity(entity)),
    routeRef: adrRouteRef,
    loader: () =>
      import('@internal/plugin-adr').then(({ AdrDashboardContent }) => (
        <AdrDashboardContent />
      )),
  },
});

const systemDiagramEntityContent = EntityContentBlueprint.make({
  name: 'system-diagram',
  params: {
    path: '/diagram',
    title: 'Diagram',
    filter: isSystemEntity,
    loader: () =>
      import('../components/catalog/EntityNativeContent').then(
        ({ SystemEntityDiagramContent }) => <SystemEntityDiagramContent />,
      ),
  },
});

/**
 * EntityContentBlueprint for @roadiehq/backstage-plugin-github-pull-requests.
 * This plugin has no alpha/new-system version, so we keep path-based registration
 * and render the package Router directly.
 */
const githubPullRequestsEntityContent = EntityContentBlueprint.make({
  name: 'github-pull-requests',
  params: {
    path: '/pull-requests',
    title: 'Pull Requests',
    filter: isGithubContentEntity,
    loader: () =>
      import('@roadiehq/backstage-plugin-github-pull-requests').then(
        ({ Router }) => <Router />,
      ),
  },
});

const githubActionsEntityContent = EntityContentBlueprint.make({
  name: 'github-actions',
  params: {
    path: '/ci-cd',
    title: 'CI/CD',
    filter: entity =>
      (isAnyComponentEntity(entity) || isDefaultPageEntity(entity)) &&
      isCicdAvailable(entity),
    routeRef: githubActionsPlugin.routes.entityContent,
    loader: () =>
      import('../components/catalog/EntityNativeContent').then(
        ({ EntityCiCdContent }) => <EntityCiCdContent />,
      ),
  },
});

const githubIssuesEntityContent = EntityContentBlueprint.make({
  name: 'github-issues',
  params: {
    path: '/github-issues',
    title: 'GitHub Issues',
    filter: isGithubContentEntity,
    routeRef: githubIssuesPlugin.routes.entityContent,
    loader: () =>
      import('@backstage-community/plugin-github-issues').then(
        ({ GithubIssuesPage }) => <GithubIssuesPage />,
      ),
  },
});

const datadogEntityContent = EntityContentBlueprint.make({
  name: 'datadog',
  params: {
    path: '/datadog',
    title: 'Datadog',
    filter: isDatadogEntity,
    // Roadie alpha exposes routes as RouteRef | SubRouteRef; this extension point
    // requires a RouteRef. Datadog's entityContent route is a top-level route.
    routeRef: datadogPlugin.routes.entityContent as any,
    loader: () =>
      import('@roadiehq/backstage-plugin-datadog').then(
        ({ EntityDatadogContent }) => <EntityDatadogContent />,
      ),
  },
});

/**
 * Single source of truth for entity tab availability.
 * Each entry mirrors the `title` and `filter` of the EntityContentBlueprint defined
 * above. Import this in entityContextMenuModule to drive the "Manage tab
 * visibility" dialog without duplicating the per-entity predicate logic.
 */
export const entityTabDefs: ReadonlyArray<{
  title: string;
  filter: (entity: Entity) => boolean;
}> = [
  // ── Per-kind primary tabs (shown first for their kind) ───────────────────
  { title: 'Definition', filter: isApiEntity },
  { title: 'Docs', filter: isTechDocsEntity },
  {
    title: 'Overview',
    filter: e =>
      isAnyComponentEntity(e) ||
      isApiEntity(e) ||
      isKindValue(e, 'user') ||
      isKindValue(e, 'group') ||
      isSystemEntity(e) ||
      isDomainEntity(e) ||
      isAgentEntity(e) ||
      isDefaultPageEntity(e),
  },
  { title: 'Diagram', filter: isSystemEntity },
  // ── Feature-gated tabs (ordered to match legacy EntityPage) ───────────────
  {
    title: 'Feedback',
    filter: e =>
      isAnyComponentEntity(e) ||
      isApiEntity(e) ||
      isAgentEntity(e) ||
      isDefaultPageEntity(e),
  },
  {
    title: 'Score card',
    filter: e => isAnyComponentEntity(e) || isDefaultPageEntity(e),
  },
  { title: 'Datadog', filter: isDatadogEntity },
  {
    title: 'CI/CD',
    filter: e =>
      (isAnyComponentEntity(e) || isDefaultPageEntity(e)) && isCicdAvailable(e),
  },
  { title: 'Pull Requests', filter: isGithubContentEntity },
  { title: 'GitHub Issues', filter: isGithubContentEntity },
  {
    title: 'Code Insights',
    filter: e =>
      isGithubContentAvailable(e) &&
      (isAnyComponentEntity(e) || isApiEntity(e) || isDefaultPageEntity(e)),
  },
  {
    title: 'Dependabot',
    filter: e =>
      isGithubContentAvailable(e) &&
      (isComponentTypeValue(e, 'website') || isApiEntity(e)),
  },
  { title: 'GraphiQL', filter: isGraphqlTabAvailable },
  { title: 'Q&A', filter: isQetaEntity },
  {
    title: 'ADRs',
    filter: e =>
      isAdrAvailable(e) && (isAnyComponentEntity(e) || isDefaultPageEntity(e)),
  },
  {
    title: 'Tech Radar',
    filter: e =>
      (isAnyComponentEntity(e) || isDefaultPageEntity(e)) &&
      isTechRadarAvailable(e),
  },
  { title: 'DORA Metrics', filter: isDevOpsMetricsEntity },
  { title: 'Config', filter: isYamlEditorEntity },
  // ── Service / website infrastructure tabs ────────────────────────────────
  { title: 'API', filter: isServiceComponentEntity },
  { title: 'Dependencies', filter: isServiceOrWebsiteComponentEntity },
  { title: 'ECS', filter: isEcsAvailable },
  {
    title: 'Kubernetes',
    filter: e =>
      isServiceOrWebsiteComponentEntity(e) && isKubernetesAvailable(e),
  },
];

/** Entity tabs migrated off legacy EntityPage JSX (attached to catalog plugin). */
export const entityContentExtensionsModule = createFrontendModule({
  pluginId: 'catalog',
  extensions: [
    // ── Per-kind default routes (path "/") ──────────────────────────────
    componentOverviewEntityContent,
    apiDefinitionEntityContent,
    apiOverviewEntityContent,
    userOverviewEntityContent,
    groupOverviewEntityContent,
    systemOverviewEntityContent,
    domainOverviewEntityContent,
    agentOverviewEntityContent,
    defaultPageOverviewEntityContent,

    // ── Common developer tabs (most-visited first) ───────────────────────
    docsEntityContent,
    githubActionsEntityContent, // CI/CD
    githubPullRequestsEntityContent, // Pull Requests
    githubIssuesEntityContent, // GitHub Issues
    codeInsightsEntityContent, // Code Insights

    // ── Structure / dependency tabs ──────────────────────────────────────
    componentApiEntityContent, // API (service/website)
    dependenciesEntityContent, // Dependencies
    dependabotEntityContent, // Dependabot (website/api)
    systemDiagramEntityContent, // Diagram (system)
    graphiqlEntityContent, // GraphiQL (graphql API)

    // ── Infrastructure tabs ──────────────────────────────────────────────
    kubernetesEntityContent,
    ecsEntityContent,
    datadogEntityContent,

    // ── Quality / governance tabs ────────────────────────────────────────
    feedbackEntityContent,
    qetaEntityContent, // Q&A
    adrEntityContent, // ADRs
    scoreCardEntityContent,
    techRadarEntityContent,

    // ── Operational / config tabs ────────────────────────────────────────
    devopsMetricsEntityContent,
    yamlEditorEntityContent, // Config
  ],
});
